import { dismissAnnouncement, getAnnouncements } from "./announcementsApi";
import { notifyAnnouncementsChanged } from "./announcementsChanged";

declare global {
  interface Window {
    CompanyData?: { Id?: number; CID?: number; Name?: string };
    payroll_announcements?: {
      dismiss(announcementId: string): Promise<void>;
      dismissFor(target: string): Promise<number>;
    };
  }
}

// Deliberately not the bar's own prop: a caller reaching for this is usually somewhere else on the
// page, and may run when no bar is mounted at all. The layout publishes the open company for every
// page, which is the same value the bar was given.
function openCompanyId(): number | null {
  const company = window.CompanyData;
  if (!company) return null;

  return company.Id ?? company.CID ?? null;
}

/**
 * Dismiss one announcement the caller already knows. Silent when there is no company open or the
 * server refuses — dismissing is never the point of whatever the caller was actually doing.
 */
export async function dismissById(announcementId: string): Promise<void> {
  const companyId = openCompanyId();
  if (!companyId) return;

  try {
    await dismissAnnouncement(companyId, announcementId);
    notifyAnnouncementsChanged();
  } catch {
    // The bar is still showing it, and the next page load re-reads the mailbox.
  }
}

/**
 * Dismiss whatever was pointing at a place, because the user is now standing in it. Catches the
 * ones who never clicked the bar — they used the menu, or a bookmark, or were already there.
 *
 * Returns how many were dismissed, which is what makes it testable from the outside.
 */
export async function dismissByTarget(target: string): Promise<number> {
  const companyId = openCompanyId();
  if (!companyId) return 0;

  try {
    const entries = await getAnnouncements(companyId);
    const pointingHere = entries.filter(entry => entry.visible && entry.linkTarget === target);

    for (const entry of pointingHere) {
      await dismissAnnouncement(companyId, entry.id);
    }

    if (pointingHere.length > 0) notifyAnnouncementsChanged();

    return pointingHere.length;
  } catch {
    return 0;
  }
}
